import { SizeStyleValues, sizeStyles } from "./sizeStyles";

const FLUSHED_BORDER_BASE = {
  border: "none",
  borderBottom: "1px solid",
  borderColor: "inherit",
  borderRadius: "0",
};

const FLUSHED_ACTIVE_BOTTOM_BORDER = {
  borderBottom: "2px solid var(--chakra-colors-brand-500)",
};

// Selector for a react-select that has a value but is not focused/open/hovered —
// used to keep the border in its resting state rather than the active state.
const reactSelectRestingWithValue =
  "& .chakra-react-select--has-value " +
  ".chakra-react-select__control:not(.chakra-react-select__control--is-focused)" +
  ":not(.chakra-react-select__control--menu-is-open):not(:hover)";

function buildContainerStyles(styles: SizeStyleValues, { transparent = false }: { transparent?: boolean } = {}) {
  return {
    // ─── Suppress any focus ring on the container itself ─────────────────────────
    "&:focus-within": {
      outline: "none !important",
      boxShadow: "none !important",
      border: "none !important",
    },

    // ─── Suppress browser focus-visible outline on all children ─────────────────
    "& *:focus-visible": {
      outline: "none !important",
    },

    // ─── React-select border styles ─────────────────────────────────────────────
    // Owned here so FloatingField doesn't silently depend on the global
    // FloatingField theme rule for its border appearance.
    "& .chakra-react-select__control": {
      ...FLUSHED_BORDER_BASE,
      outline: "none !important",
      boxShadow: "none !important",
    },
    "& .chakra-react-select__control:hover:not(.chakra-react-select__control--is-focused)": {
      ...FLUSHED_BORDER_BASE,
    },
    "& .chakra-react-select__control--is-focused, & .chakra-react-select__control.chakra-react-select__control--is-focused:hover":
      {
        border: "none",
        boxShadow: "none",
        outline: "none",
        ...FLUSHED_ACTIVE_BOTTOM_BORDER,
      },
    [reactSelectRestingWithValue]: {
      ...FLUSHED_BORDER_BASE,
    },
    "& .chakra-react-select__value-container:not(.chakra-react-select__value-container--is-multi)": {
      height: "100%",
    },

    // ─── Label float on focus ────────────────────────────────────────────────────
    "&:focus-within .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },

    // ─── Label float via CSS :has() — fires before React paint ──────────────────
    // These handle the initial render when a field already has a value, avoiding
    // the flash that useLayoutEffect would cause in React 18 concurrent mode.
    "&:has(.floating-single-select .chakra-react-select__single-value) .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },
    "&:has(.timezone .chakra-react-select__single-value) .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },
    "&:has(.floating-multi-select .chakra-react-select__multi-value) .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },
    "&:has(.chakra-button.floating-button.has-value) .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },
    "&:has(.floating-textarea:not(:placeholder-shown)) .cds-floating-label": {
      transform: `scale(0.75) translateY(${styles.translateY})`,
    },

    // ─── Multi-select grows with tags ────────────────────────────────────────────
    "&:has(.floating-multi-select)": {
      height: "auto",
    },
    "& .floating-multi-select .chakra-react-select__control": {
      minHeight: styles.inputHeight,
      alignItems: "flex-start",
    },
    "& .floating-multi-select .chakra-react-select__indicators": {
      paddingTop: styles.multiSelectIndicatorPaddingTop,
    },
    "& .floating-multi-select .chakra-react-select__value-container--is-multi": {
      minHeight: styles.inputHeight,
    },
    "& .floating-multi-select .chakra-react-select__value-container": {
      paddingTop: styles.paddingTop,
      paddingLeft: styles.multiSelectPaddingLeft,
      paddingInlineStart: styles.multiSelectPaddingLeft,
      alignItems: "flex-start",
    },

    // ─── Textarea auto-height ────────────────────────────────────────────────────
    "&:has(.floating-textarea)": {
      height: "auto",
    },
    "& .floating-textarea": {
      minHeight: styles.inputHeight,
      paddingLeft: "8px",
      paddingInlineStart: "8px",
    },

    // ─── Trix editor auto-height ─────────────────────────────────────────────────
    // Also clear the container bg — trix-editor owns its own background directly.
    "&:has(.trix-container)": {
      height: "auto",
      backgroundColor: "transparent",
    },

    // ─── Trix toolbar & editor ───────────────────────────────────────────────────
    // FloatingTrix passes toolbarBgColor="transparent" so --trix-toolbar-bg is set
    // as an inline style (highest specificity), making buttons show the container bg.

    // Offset the label to align with trix-editor text (matches legacy paddingLeft: 12px).
    "&:has(.trix-container) .cds-floating-label": {
      left: "12px",
    },
    // paddingTop clears the floating label; paddingLeft aligns text with label.
    // Transparent variant has no floating label and no tinted bg — the host (e.g.
    // ComposeBox body) owns padding + surface color, so skip these overrides.
    "& .trix-container trix-editor": transparent
      ? {}
      : {
          paddingTop: "20px",
          paddingLeft: "12px",
        },
    // Editable unfocused: darker bg directly on the editor element.
    "& .trix-container trix-editor.is-editable": transparent
      ? { backgroundColor: "transparent" }
      : { backgroundColor: "bg.tertiary" },
    // Focused: switch to input bg (white in light mode).
    "& .trix-container trix-editor.is-editable.is-focused": transparent
      ? { backgroundColor: "transparent" }
      : { backgroundColor: "var(--chakra-colors-chakra-input-bg, white)" },

    // ─── Text inputs ─────────────────────────────────────────────────────────────
    // :not(.floating-emoji-picker *) excludes inputs inside the emoji picker popup,
    // which is a DOM descendant of the container but must keep its own styling.
    "& input:not(.chakra-react-select__input):not(.floating-emoji-picker *)": {
      ...FLUSHED_BORDER_BASE,
      height: styles.inputHeight,
      paddingTop: styles.textInputPaddingTop,
      paddingLeft: "8px",
      paddingInlineStart: "8px",
      outline: "none !important",
      boxShadow: "none !important",
    },
    "& input:not(.chakra-react-select__input):not(.floating-emoji-picker *):hover": {
      ...FLUSHED_BORDER_BASE,
    },
    "& input:not(.chakra-react-select__input):not(.floating-emoji-picker *):focus, & input:not(.chakra-react-select__input):not(.floating-emoji-picker *):focus-visible":
      {
        border: "none !important",
        boxShadow: "none !important",
        outline: "none !important",
        ...FLUSHED_ACTIVE_BOTTOM_BORDER,
      },

    // ─── Calendar trigger button ─────────────────────────────────────────────────
    // paddingTop positions the date text below the floating label.
    // Negative marginTop on the icon counteracts the paddingTop to keep it centered.
    "& .chakra-button.floating-button": {
      height: `${styles.inputHeight} !important`,
      paddingTop: `${styles.calendarPaddingTop} !important`,
      paddingLeft: "8px !important",
      paddingInlineStart: "8px !important",
      alignItems: "flex-start !important",
      fontWeight: "normal",
    },
    "& .chakra-button.floating-button .chakra-button__icon:last-child": {
      marginTop: `calc(-1 * ${styles.calendarPaddingTop} / 2 - 10px)`,
      color: "gray.400",
    },
    "& .chakra-button.floating-button .chakra-button__icon:last-child svg": {
      transform: "scale(0.8)",
    },

    // ─── Timezone select ─────────────────────────────────────────────────────────
    // paddingTop on the value-container positions all content (single-value + cursor).
    // paddingTop:0 and marginTop:0 on inner elements neutralise global FloatingField
    // rules that also set these — without the overrides the values stack and overflow.
    //
    // Note: paddingLeft/paddingInlineStart are intentionally NOT zeroed here (unlike
    // floating-single-select). TimezoneSelect renders its value with a small built-in
    // left offset (timezoneValuePaddingLeft) that aligns it correctly — removing the
    // padding would push the text too far left.
    "& .timezone .chakra-react-select__value-container": {
      paddingTop: styles.timezoneValuePaddingTop,
      paddingBottom: "0",
      alignItems: "flex-start",
    },
    "& .timezone .chakra-react-select__single-value": {
      height: "auto",
      paddingTop: "0",
      paddingLeft: styles.timezoneValuePaddingLeft,
      fontSize: styles.fontSize,
    },
    "& .timezone input.chakra-react-select__input": {
      marginTop: "0",
      marginLeft: styles.timezoneValuePaddingLeft,
    },

    // ─── Single-value react-select ───────────────────────────────────────────────
    // Same pattern as timezone — paddingTop on the container positions both the
    // value div and the typing cursor. Inner overrides neutralise global values.
    "& .floating-single-select .chakra-react-select__value-container": {
      paddingTop: styles.singleValuePaddingTop,
      paddingBottom: "0",
      paddingLeft: "0",
      paddingInlineStart: "0",
      alignItems: "flex-start",
    },
    "& .floating-single-select .chakra-react-select__single-value": {
      height: "auto",
      paddingTop: "0",
      paddingLeft: styles.singleValuePaddingLeft,
      fontSize: styles.fontSize,
    },
    "& .floating-single-select input.chakra-react-select__input": {
      marginTop: "0",
      marginLeft: styles.singleValuePaddingLeft,
    },
  };
}

export const containerStyles = {
  md: buildContainerStyles(sizeStyles.md),
  sm: buildContainerStyles(sizeStyles.sm),
};

export const transparentContainerStyles = {
  md: buildContainerStyles(sizeStyles.md, { transparent: true }),
  sm: buildContainerStyles(sizeStyles.sm, { transparent: true }),
};
